import { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function readCssVar(name, fallback) {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function useChartColors() {
  const [colors, setColors] = useState(() => ({
    imp: "#002FA7",
    exp: "#0284C7",
    grid: "#e4e4e7",
    axis: "#52525b",
    bg: "#ffffff",
    border: "#e4e4e7",
  }));
  useEffect(() => {
    const update = () =>
      setColors({
        imp: readCssVar("--doe-import", "#002FA7"),
        exp: readCssVar("--doe-export", "#0284C7"),
        grid: readCssVar("--grid-line", "#e4e4e7"),
        axis: readCssVar("--axis-text", "#52525b"),
        bg: `hsl(${readCssVar("--background", "0 0% 100%")})`,
        border: `hsl(${readCssVar("--border", "240 6% 90%")})`,
      });
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return colors;
}

export default function DOEChart({ timeSeries }) {
  const c = useChartColors();
  const data = timeSeries.map((t) => ({
    step: t.time_step,
    hour: `${String(t.hour).padStart(2, "0")}:00`,
    import: t.total_import_mw,
    export: t.total_export_mw,
    minV: t.min_voltage,
    maxV: t.max_voltage,
  }));

  return (
    <div
      className="border border-border rounded-[2px] bg-background p-4 sm:p-6"
      data-testid="doe-chart"
    >
      <div className="h-[380px] min-h-[380px] w-full">
        <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={300}>
          <AreaChart data={data} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
            <defs>
              <linearGradient id="impFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c.imp} stopOpacity={0.28} />
                <stop offset="100%" stopColor={c.imp} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="expFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c.exp} stopOpacity={0.28} />
                <stop offset="100%" stopColor={c.exp} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={c.grid} strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="hour"
              tick={{ fontFamily: "IBM Plex Mono", fontSize: 11, fill: c.axis }}
              tickLine={false}
              axisLine={{ stroke: c.grid }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontFamily: "IBM Plex Mono", fontSize: 11, fill: c.axis }}
              tickLine={false}
              axisLine={{ stroke: c.grid }}
              label={{
                value: "MW",
                angle: -90,
                position: "insideLeft",
                style: { fontFamily: "IBM Plex Mono", fontSize: 11, fill: c.axis },
              }}
            />
            <Tooltip
              contentStyle={{
                fontFamily: "IBM Plex Mono",
                fontSize: 12,
                borderRadius: 2,
                border: `1px solid ${c.border}`,
                background: c.bg,
              }}
              formatter={(v, name) => [
                `${Number(v).toFixed(2)} MW`,
                name === "import" ? "Import capacity" : "Export capacity",
              ]}
              labelFormatter={(l) => `Hour ${l}`}
            />
            <Legend
              wrapperStyle={{
                fontFamily: "IBM Plex Sans",
                fontSize: 12,
                paddingTop: 8,
                color: c.axis,
              }}
              formatter={(value) =>
                value === "import" ? "Import capacity (MW)" : "Export capacity (MW)"
              }
            />
            <Area
              type="monotone"
              dataKey="import"
              stroke={c.imp}
              strokeWidth={2}
              fill="url(#impFill)"
              dot={{ r: 2.5, fill: c.imp, strokeWidth: 0 }}
              activeDot={{ r: 4 }}
            />
            <Area
              type="monotone"
              dataKey="export"
              stroke={c.exp}
              strokeWidth={2}
              fill="url(#expFill)"
              dot={{ r: 2.5, fill: c.exp, strokeWidth: 0 }}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
